import { CheckCircle2Icon, SparklesIcon, TriangleAlertIcon } from 'lucide-react'
import { Button } from '@app/components/ui/button'
import { cn } from '@app/lib/cn'
import type { SuggestModelUsed, TraceReview } from './api'
import { traceFindingLine, traceReviewSummary, TRACE_REVIEW_OK_TEXT } from './suggest'

/**
 * What the model made of OUR drawing, under the sheet it is about.
 *
 * Four states, one strip, deliberately small. The screen already carries a
 * verdict bar, a quality-flag count, an approval list and a rail of per-piece
 * warnings; a fourth loud box would be one more thing to scroll past. So:
 *
 *   • running — a quiet grey line, because a spinner on a sheet nobody is
 *     waiting on is noise. The operator can label straight through it.
 *   • no verdict — the same quiet grey line: a model answered about the pieces
 *     and said nothing usable about the drawing. Worth one muted sentence
 *     because the suggestions in the rail came from somewhere, and this strip
 *     is where the chip says from which model.
 *   • iyi     — one green line and nothing to open. A machine agreeing with the
 *     tracer is worth one line and not a paragraph.
 *   • sorunlu — amber, collapsed, with the count in the spine. Open it and the
 *     findings are a plain list of sentences the model wrote in Turkish.
 *
 * It is a REPORT. Nothing here changes a contour or blocks a save: this screen
 * has no repair tool by design (a trace that is wrong is a scan that was
 * wrong), so the whole job of a finding is to send somebody back to the picture
 * with something specific to look for.
 *
 * `<details>` rather than state: the browser owns "is it open", the note keeps
 * that across a re-render it would otherwise lose, and it is keyboard-operable
 * without anything written here.
 */
/** A call that did not produce an answer, and whether asking again could help. */
export interface TraceReviewError {
  text: string
  /**
   * False for a failure no press can fix — the environment has NEITHER suggest
   * transport (no xAI key and no Workers AI binding), so the feature is switched
   * off rather than unwell. The note then goes quiet and loses its button: an
   * amber box with a dead retry on every sheet of every set is how a
   * switched-off feature turns into something people learn to ignore, including
   * on the sheets where it works.
   */
  canRetry: boolean
}

/**
 * Which model answered, as one muted word beside its verdict.
 *
 * There because the fallback is invisible otherwise: the server asks grok, and
 * on a vendor outage answers from Workers AI's Scout inside the same request. A
 * quieter reading on a Thursday afternoon is then a mystery nobody can settle
 * without the Worker's logs — and this chip settles it in one glance.
 *
 * Deliberately not a colour, not a badge and not a tooltip anybody has to hunt
 * for: the same muted micro-type as "aç / kapat" next to it. It is a fact about
 * the note, not a warning about the sheet, and the operator has nothing to do
 * about it either way.
 */
function ModelChip({ modelUsed }: { modelUsed: SuggestModelUsed }) {
  return (
    <span className="ml-auto shrink-0 font-mono text-ink-muted" title="Yanıtlayan model">
      {modelUsed}
    </span>
  )
}

export function TraceReviewNote({
  review,
  modelUsed,
  running,
  error,
  onRetry,
  className,
}: {
  /** The answer, or null while there is none for this sheet. */
  review: TraceReview | null
  /** Which model produced that answer, or null when there is none. */
  modelUsed: SuggestModelUsed | null
  running: boolean
  /** Why there is no answer, or null. */
  error: TraceReviewError | null
  onRetry: () => void
  className?: string
}) {
  if (running) {
    return (
      <p
        className={cn(
          'flex items-center gap-1.5 rounded-sm border border-hairline px-3 py-1.5 text-micro text-ink-muted',
          className,
        )}
      >
        <SparklesIcon className="size-3 shrink-0 animate-pulse" aria-hidden />
        Yapay zekâ inceliyor…
      </p>
    )
  }

  if (error) {
    if (!error.canRetry) {
      return (
        <p
          className={cn(
            'flex items-center gap-1.5 rounded-sm border border-hairline px-3 py-1.5 text-micro text-ink-muted',
            className,
          )}
        >
          <SparklesIcon className="size-3 shrink-0" aria-hidden />
          {error.text}
        </p>
      )
    }
    return (
      <p
        role="alert"
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-sm border border-hardal/30 bg-hardal/10 px-3 py-1.5 text-micro text-hardal',
          className,
        )}
      >
        <TriangleAlertIcon className="size-3 shrink-0" aria-hidden />
        {error.text}
        <Button variant="ghost" size="sm" className="h-auto px-1.5 py-0.5" onClick={onRetry}>
          Tekrar dene
        </Button>
      </p>
    )
  }

  if (!review) {
    /*
     * A model answered, and its reading of the PIECES is already in the rail —
     * but it said nothing usable about our drawing (a malformed review is
     * dropped rather than defaulted to "iyi", see the Worker's parser). That
     * state used to be silent, and silence was honest while one model was the
     * only possible reader. It is not honest now: the fallback pre-fills
     * controls too, and the one word saying who did it must not be the thing
     * that goes missing.
     */
    if (!modelUsed) return null
    return (
      <p
        className={cn(
          'flex items-center gap-1.5 rounded-sm border border-hairline px-3 py-1.5 text-micro text-ink-muted',
          className,
        )}
      >
        <SparklesIcon className="size-3 shrink-0" aria-hidden />
        Yapay zekâ parçaları okudu, çizim için görüş bildirmedi.
        <ModelChip modelUsed={modelUsed} />
      </p>
    )
  }

  if (review.overall === 'iyi') {
    return (
      <p
        className={cn(
          'flex items-center gap-1.5 rounded-sm border border-zeytin/25 bg-zeytin/8 px-3 py-1.5 text-micro text-zeytin',
          className,
        )}
      >
        <CheckCircle2Icon className="size-3 shrink-0" aria-hidden />
        {TRACE_REVIEW_OK_TEXT}
        {modelUsed ? <ModelChip modelUsed={modelUsed} /> : null}
      </p>
    )
  }

  return (
    <details
      className={cn(
        'rounded-sm border border-hardal/30 bg-hardal/10 px-3 py-1.5 text-micro text-hardal',
        className,
      )}
    >
      <summary className="flex cursor-pointer items-center gap-1.5 list-none">
        <TriangleAlertIcon className="size-3 shrink-0" aria-hidden />
        {traceReviewSummary(review)}
        {modelUsed ? <ModelChip modelUsed={modelUsed} /> : null}
        <span className={cn('text-ink-muted', modelUsed ? 'ml-2' : 'ml-auto')}>aç / kapat</span>
      </summary>
      {review.findings.length > 0 ? (
        <ul className="mt-1.5 flex flex-col gap-1 border-t border-hardal/20 pt-1.5">
          {review.findings.map((finding, index) => (
            <li key={`${finding.kind}-${index}`}>{traceFindingLine(finding)}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-1.5 border-t border-hardal/20 pt-1.5">
          Yapay zekâ bir sorun bildirdi ama gerekçesini okunur biçimde vermedi — çizimi kendiniz
          karşılaştırın.
        </p>
      )}
    </details>
  )
}
